import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../../config.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import Notification from "../../components/Notification.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatToDayMonthYear, formatTimestamp } from "../../utils/TextUtils.js";

function Payments() {
    const { id } = useParams();
    const { periodId, routeId } = useParams();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const perPage = 30; // Items per page
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");
    const canEdit = (userRole) => {
        return allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']);
    };

    const navigate = useNavigate();

    // Fetch payments for the payment period
    const fetchPayments = useCallback(async () => {
        try {
            setLoading(true);
            let url = `${API_URL}/payment-periods/${id}/route/${routeId}?page=${currentPage}&limit=${perPage}`;
            if (searchTerm) {
                url += `&search=${searchTerm}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setPayments(data.data || []);
                setTotalPages(data.last_page || 1);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch payments");
            }
        } catch (error) {
            setError("An error occurred while fetching payments.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, navigate, token, id]);

    // Fetch payments when dependencies change
    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null); // Close dropdown if the same one is clicked
        } else {
            setIsDropdownOpen(index); // Open the clicked dropdown
        }
    };

    const handleDeleteClick = (payment) => {
        setSelectedPayment(payment);
        setIsConfirmDialogOpen(true);
    };

    const confirmDeletePayment = async () => {
        if (selectedPayment) {
            try {
                const token = localStorage.getItem("access_token");
                const response = await fetch(`${API_URL}/payments/${selectedPayment.id}`, {
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    setNotification({ type: "success", message: 'Payment deleted successfully' });
                    fetchPayments(); // Refresh the list
                } else if (response.status === 401) {
                    navigate("/logout");
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: 'Payment not found.' });
                } else {
                    setNotification({ type: "danger", message: 'There was an error.' });
                    throw new Error('Failed to delete payment');
                }
            } catch (error) {
                setNotification({ message: 'An error occurred while deleting the payment.', type: 'danger' });
                console.error("Error:", error);
            } finally {
                setIsConfirmDialogOpen(false);
                setSelectedPayment(null);
            }
        }
    };

    const cancelDeletePayment = () => {
        setIsConfirmDialogOpen(false);
        setSelectedPayment(null);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1); // Reset to the first page when searching
    };

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <p className="title">Payments</p>
                        </div>
                    </div>
                    <div className="level-right">
                        {canEdit(userRole) && (
                            <p className="level-item">
                                <Link to="add" className="button primary-bg">
                                    <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New
                                </Link>
                            </p>
                        )}
                    </div>
                </nav>

                <h1>Payments List</h1>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {/* Search Bar */}
                <div className="field">
                    <label className="label">Search by Farmer Name</label>
                    <div className="control">
                        <input
                            className="input"
                            type="text"
                            placeholder="Search by farmer name"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </div>

                {loading ? (
                    <p>Loading payments...</p>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <>
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Farmer Name</th>
                                    <th> Milk Price</th>
                                    <th> Debt</th>
                                    <th>Arrears</th>
                                    <th>Total Income</th>
                                    <th>Amount To Pay </th>
                                    <th>Status</th>
                                    <th>Amount Paid</th>
                                    <th>Paid At</th>
                                    <th>Options</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.length > 0 ? (
                                    payments.map((payment, index) => (
                                        <tr key={payment.id}>
                                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                                            <td>
                                                <Link to={`/dashboard/farmers/${payment?.farmer?.id}/details`} className="primary-text-color">
                                                    {payment?.farmer?.first_name} {payment?.farmer?.middle_name} {payment?.farmer?.last_name} ({payment?.farmer?.farmer_number})
                                                </Link>
                                            </td>
                                            <td>{payment.total_milk_price}</td>
                                            <td>{payment.total_debt}</td>
                                            <td>{payment.arrears}</td>
                                            <td>{payment.total_income}</td>
                                            <td>{payment.amount_to_pay}</td>
                                            <td>
                                                {payment.status === 'Unpaid' && (
                                                    <span className="tag is-danger">Unpaid</span>
                                                )}
                                                {payment.status === 'Partially Paid' && (
                                                    <span className="tag is-warning">Partially Paid</span>
                                                )}
                                                {payment.status === 'Paid' && (
                                                    <span className="tag is-success">Paid</span>
                                                )}
                                            </td>
                                            <td>{payment.amount_paid}</td> 
                                            <td>{payment.paid_at ? formatTimestamp(payment.paid_at) : 'N/A'}</td>
                                            <td>
                                                <Link to=""><span className="fas fa-eye"></span></Link>
                                               
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="15" className="has-text-centered">No payments found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        <nav className="pagination is-centered">
                            <button
                                className="pagination-previous"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="pagination-page">Page {currentPage} of {totalPages}</span>
                            <button
                                className="pagination-next"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </nav>
                    </>
                )}
            </main>
        </div>
    );
}

export default Payments;